import {
  Box,
  Chip,
  Stack,
  Button,
  Divider,
  FormLabel,
  TextField,
  Typography,
  FormControl,
  CircularProgress,
} from '@mui/material';
import axios from 'axios';
import { isUuid } from 'utils';
import { useParams } from 'react-router-dom';
import apiService from 'library/api-service';
import { TICKET_FEEDBACK_STATUS } from 'enums';
import { FormattedMessage, useIntl } from 'react-intl';
import { FreshdeskFeedbackTicket } from 'library/types';
import SendRoundedIcon from '@mui/icons-material/SendRounded';
import ShieldOutlinedIcon from '@mui/icons-material/ShieldOutlined';
import TaskAltRoundedIcon from '@mui/icons-material/TaskAltRounded';
import { FormEvent, useEffect, useMemo, useRef, useState } from 'react';
import SupportAgentRoundedIcon from '@mui/icons-material/SupportAgentRounded';
import { AnsweredFeedback, SupportRating, SurveyResult, SurveyShell, TicketDetails } from './components';
import { RATING_VALUES, REDIRECT_DELAY_SECONDS, RatingLabels, RatingValue, redirectToIdeconWebsite } from './constants';

type PageState = 'loading' | 'ready' | 'submitting';
type ResultKind = 'invalid-link' | 'not-found' | 'load-error' | 'success' | 'submit-error';

type ResultCopy = {
  tone: 'success' | 'error';
  title: { id: string; defaultMessage: string };
  message: { id: string; defaultMessage: string };
};

const RESULT_COPY: Record<ResultKind, ResultCopy> = {
  'invalid-link': {
    tone: 'error',
    title: { id: 'ticketSurvey.invalidTitle', defaultMessage: 'Geçersiz bağlantı' },
    message: {
      id: 'ticketSurvey.invalidMessage',
      defaultMessage: 'Anket bağlantısı geçersiz veya eksik. Lütfen size gönderilen bağlantıyı kontrol edin.',
    },
  },
  'not-found': {
    tone: 'error',
    title: { id: 'ticketSurvey.notFoundTitle', defaultMessage: 'Ticket Bulunamadı' },
    message: {
      id: 'ticketSurvey.notFoundMessage',
      defaultMessage: 'Bu bağlantıya ait bir destek kaydı bulunamadı veya anket artık kullanılamıyor.',
    },
  },
  'load-error': {
    tone: 'error',
    title: { id: 'ticketSurvey.loadErrorTitle', defaultMessage: 'Bilgiler yüklenemedi' },
    message: {
      id: 'ticketSurvey.loadErrorMessage',
      defaultMessage: 'Şu anda destek kaydınıza ulaşamıyoruz. Lütfen daha sonra yeniden deneyin.',
    },
  },
  success: {
    tone: 'success',
    title: { id: 'ticketSurvey.successTitle', defaultMessage: 'Teşekkür ederiz' },
    message: {
      id: 'ticketSurvey.successMessage',
      defaultMessage:
        'Geri bildiriminiz başarıyla alındı. Görüşünüz, hizmet kalitemizi geliştirmemize yardımcı olacak.',
    },
  },
  'submit-error': {
    tone: 'error',
    title: { id: 'ticketSurvey.submitErrorTitle', defaultMessage: 'Geri bildiriminiz gönderilemedi' },
    message: {
      id: 'ticketSurvey.submitErrorMessage',
      defaultMessage: 'İşlem sırasında bir sorun oluştu. Dilerseniz daha sonra bağlantıyı yeniden kullanabilirsiniz.',
    },
  },
};

const getLoadErrorResult = (error: unknown): ResultKind => {
  if (!axios.isAxiosError(error)) return 'load-error';

  const status = error.response?.status;
  if (status === 400 || status === 404) return 'not-found';

  return 'load-error';
};

const TicketSurvey = () => {
  const intl = useIntl();
  const { uniqueKey } = useParams<{ uniqueKey?: string }>();
  const isSubmittingRef = useRef(false);
  const submitControllerRef = useRef<AbortController | null>(null);

  const [rate, setRate] = useState<RatingValue | 0>(0);
  const [hoveredRate, setHoveredRate] = useState(-1);
  const [description, setDescription] = useState('');
  const [pageState, setPageState] = useState<PageState>('loading');
  const [ticket, setTicket] = useState<FreshdeskFeedbackTicket | null>(null);
  const [result, setResult] = useState<ResultKind | null>(null);
  const [secondsUntilRedirect, setSecondsUntilRedirect] = useState(REDIRECT_DELAY_SECONDS);

  const normalizedUniqueKey = uniqueKey ?? '';
  const isSubmitting = pageState === 'submitting';

  const ratingLabels = useMemo<RatingLabels>(
    () => ({
      1: intl.formatMessage({ id: 'ticketSurvey.rating.1', defaultMessage: 'Çok kötü' }),
      2: intl.formatMessage({ id: 'ticketSurvey.rating.2', defaultMessage: 'Kötü' }),
      3: intl.formatMessage({ id: 'ticketSurvey.rating.3', defaultMessage: 'Orta' }),
      4: intl.formatMessage({ id: 'ticketSurvey.rating.4', defaultMessage: 'İyi' }),
      5: intl.formatMessage({ id: 'ticketSurvey.rating.5', defaultMessage: 'Çok iyi' }),
    }),
    [intl]
  );

  const activeRating = hoveredRate > 0 ? hoveredRate : rate;

  useEffect(() => {
    submitControllerRef.current?.abort();
    setRate(0);
    setHoveredRate(-1);
    setDescription('');
    setTicket(null);
    setPageState('loading');
    setResult(null);
    isSubmittingRef.current = false;

    if (!normalizedUniqueKey || !isUuid(normalizedUniqueKey)) {
      setResult('invalid-link');
      return () => submitControllerRef.current?.abort();
    }

    const controller = new AbortController();
    let isActive = true;

    const loadTicket = async () => {
      try {
        const response = await apiService.getFreshdeskFeedback(normalizedUniqueKey, controller.signal);

        if (!isActive) return;

        const hasKnownStatus =
          response.data?.status === TICKET_FEEDBACK_STATUS.SENT ||
          response.data?.status === TICKET_FEEDBACK_STATUS.ANSWERED;

        if (response.isSuccess && response.data && hasKnownStatus) {
          setTicket(response.data);
          setPageState('ready');
          return;
        }

        setResult('load-error');
      } catch (error) {
        if (!isActive || axios.isCancel(error)) return;

        setResult(getLoadErrorResult(error));
      }
    };

    loadTicket();

    return () => {
      isActive = false;
      controller.abort();
      submitControllerRef.current?.abort();
    };
  }, [normalizedUniqueKey]);

  useEffect(() => {
    if (!result) return;

    setSecondsUntilRedirect(REDIRECT_DELAY_SECONDS);

    const countdownId = window.setInterval(() => {
      setSecondsUntilRedirect((current) => Math.max(0, current - 1));
    }, 1000);
    const redirectId = window.setTimeout(() => {
      redirectToIdeconWebsite();
    }, REDIRECT_DELAY_SECONDS * 1000);

    return () => {
      window.clearInterval(countdownId);
      window.clearTimeout(redirectId);
    };
  }, [result]);

  const handleRatingChange = (_event: unknown, newValue: number | null) => {
    if (newValue && RATING_VALUES.includes(newValue as RatingValue)) {
      setRate(newValue as RatingValue);
    }
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (
      !rate ||
      ticket?.status !== TICKET_FEEDBACK_STATUS.SENT ||
      isSubmittingRef.current ||
      !isUuid(normalizedUniqueKey)
    )
      return;

    const submitController = new AbortController();
    submitControllerRef.current = submitController;
    isSubmittingRef.current = true;
    setPageState('submitting');

    try {
      const response = await apiService.submitFreshdeskFeedback(
        {
          uniqueKey: normalizedUniqueKey,
          rate,
          description: description.trim(),
        },
        submitController.signal
      );

      if (submitController.signal.aborted) return;

      setResult(response.isSuccess ? 'success' : 'submit-error');
    } catch (error) {
      if (axios.isCancel(error) || submitController.signal.aborted) return;

      setResult('submit-error');
    } finally {
      if (submitControllerRef.current === submitController) {
        submitControllerRef.current = null;
      }
    }
  };

  const renderContent = () => {
    if (result) {
      const copy = RESULT_COPY[result];

      return (
        <SurveyResult
          tone={copy.tone}
          title={intl.formatMessage(copy.title)}
          message={intl.formatMessage(copy.message)}
          secondsUntilRedirect={secondsUntilRedirect}
        />
      );
    }

    if (pageState === 'loading' || !ticket) {
      return (
        <Box
          role="status"
          aria-live="polite"
          aria-busy="true"
          sx={{ display: 'grid', minHeight: 420, placeItems: 'center', px: 3, py: 8, textAlign: 'center' }}>
          <Stack alignItems="center" spacing={2}>
            <CircularProgress size={38} thickness={4} />
            <Typography color="text.secondary">
              <FormattedMessage id="ticketSurvey.loading" defaultMessage="Destek kaydınız yükleniyor…" />
            </Typography>
          </Stack>
        </Box>
      );
    }

    const requesterName =
      ticket.requesterName ||
      intl.formatMessage({
        id: 'ticketSurvey.requesterFallback',
        defaultMessage: 'değerli müşterimiz',
      });
    const isAnswered = ticket.status === TICKET_FEEDBACK_STATUS.ANSWERED;
    const headerCopy = isAnswered
      ? {
          kicker: intl.formatMessage({
            id: 'ticketSurvey.answeredKicker',
            defaultMessage: 'Değerlendirme tamamlandı',
          }),
          title: intl.formatMessage({
            id: 'ticketSurvey.answeredTitle',
            defaultMessage: 'Değerlendirmeniz',
          }),
          intro: intl.formatMessage(
            {
              id: 'ticketSurvey.answeredIntro',
              defaultMessage:
                'Merhaba {name}, bu destek kaydı için daha önce paylaştığınız değerlendirmeyi aşağıda görebilirsiniz.',
            },
            { name: requesterName }
          ),
        }
      : {
          kicker: intl.formatMessage({
            id: 'ticketSurvey.kicker',
            defaultMessage: 'Destek değerlendirmesi',
          }),
          title: intl.formatMessage({
            id: 'ticketSurvey.title',
            defaultMessage: 'Desteğimizi değerlendirin',
          }),
          intro: intl.formatMessage(
            {
              id: 'ticketSurvey.intro',
              defaultMessage: 'Merhaba {name}, size sunduğumuz destek deneyimiyle ilgili görüşünüzü paylaşır mısınız?',
            },
            { name: requesterName }
          ),
        };

    return (
      <>
        <Box
          sx={(theme) => ({
            px: { xs: 2.5, sm: 4 },
            pt: { xs: 3, sm: 4 },
            pb: { xs: 2.5, sm: 3 },
            background: `linear-gradient(135deg, ${theme.palette.action.selected}, transparent 72%)`,
          })}>
          <Chip
            size="small"
            color={isAnswered ? 'success' : 'primary'}
            variant="outlined"
            icon={isAnswered ? <TaskAltRoundedIcon /> : <SupportAgentRoundedIcon />}
            label={headerCopy.kicker}
            sx={{ mb: 2, fontWeight: 700 }}
          />
          <Typography component="h1" variant="h4" sx={{ mb: 1 }}>
            {headerCopy.title}
          </Typography>
          <Typography color="text.secondary" sx={{ maxWidth: 720, lineHeight: 1.7 }}>
            {headerCopy.intro}
          </Typography>
        </Box>

        <Divider />

        {isAnswered ? (
          <Box sx={{ p: { xs: 2.5, sm: 4 } }}>
            <Stack spacing={{ xs: 3, sm: 3.5 }}>
              <TicketDetails ticket={ticket} />
              <AnsweredFeedback ticket={ticket} ratingLabels={ratingLabels} />
            </Stack>
          </Box>
        ) : (
          <Box component="form" noValidate onSubmit={handleSubmit} sx={{ p: { xs: 2.5, sm: 4 } }}>
            <Stack spacing={{ xs: 3, sm: 3.5 }}>
              <TicketDetails ticket={ticket} />

              <FormControl component="fieldset" required fullWidth>
                <FormLabel
                  component="legend"
                  id="ticket-survey-rating-label"
                  sx={{ mb: 1.5, color: 'text.primary', fontWeight: 700 }}>
                  <FormattedMessage
                    id="ticketSurvey.ratingQuestion"
                    defaultMessage="Aldığınız desteği nasıl değerlendirirsiniz?"
                  />
                </FormLabel>
                <SupportRating
                  name="support-rating"
                  value={rate}
                  disabled={isSubmitting}
                  onChange={handleRatingChange}
                  onChangeActive={(_event, value) => setHoveredRate(value)}
                  getLabelText={(value) => `${value} / 5 - ${ratingLabels[value as RatingValue]}`}
                  emptyLabelText={intl.formatMessage({
                    id: 'ticketSurvey.ratingRequired',
                    defaultMessage: 'Lütfen 1 ile 5 arasında bir puan seçin.',
                  })}
                  role="radiogroup"
                  aria-required="true"
                  centerOnMobile
                  aria-labelledby="ticket-survey-rating-label"
                  aria-describedby="ticket-survey-rating-help"
                />
                <Typography
                  id="ticket-survey-rating-help"
                  variant="body2"
                  color={activeRating ? 'text.primary' : 'text.secondary'}
                  sx={{
                    mt: 1.25,
                    minHeight: 20,
                    textAlign: { xs: 'center', sm: 'left' },
                    fontWeight: activeRating ? 700 : 400,
                  }}>
                  {activeRating ? (
                    `${activeRating} / 5 — ${ratingLabels[activeRating as RatingValue]}`
                  ) : (
                    <FormattedMessage
                      id="ticketSurvey.ratingRequired"
                      defaultMessage="Lütfen 1 ile 5 arasında bir puan seçin."
                    />
                  )}
                </Typography>
              </FormControl>

              <FormControl fullWidth>
                <FormLabel htmlFor="ticket-survey-description" sx={{ mb: 1, color: 'text.primary', fontWeight: 700 }}>
                  <FormattedMessage id="ticketSurvey.description" defaultMessage="Açıklama (isteğe bağlı)" />
                </FormLabel>
                <TextField
                  id="ticket-survey-description"
                  fullWidth
                  multiline
                  minRows={4}
                  value={description}
                  disabled={isSubmitting}
                  onChange={(event) => setDescription(event.target.value)}
                  placeholder={intl.formatMessage({
                    id: 'ticketSurvey.descriptionPlaceholder',
                    defaultMessage: 'Deneyiminizle ilgili eklemek istediklerinizi yazabilirsiniz.',
                  })}
                  sx={{
                    '& .MuiInputBase-root': {
                      height: 'auto',
                      alignItems: 'flex-start',
                    },
                    '& .MuiInputBase-inputMultiline': {
                      lineHeight: 1.6,
                    },
                  }}
                />
              </FormControl>

              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={2}
                alignItems={{ xs: 'stretch', sm: 'center' }}
                justifyContent="space-between">
                <Stack direction="row" spacing={1} alignItems="flex-start" sx={{ maxWidth: 390 }}>
                  <ShieldOutlinedIcon color="action" fontSize="small" aria-hidden="true" sx={{ mt: 0.1 }} />
                  <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.55 }}>
                    <FormattedMessage
                      id="ticketSurvey.privacyNote"
                      defaultMessage="Yanıtınız yalnızca destek kalitemizi değerlendirmek ve geliştirmek amacıyla kullanılacaktır."
                    />
                  </Typography>
                </Stack>

                <Button
                  type="submit"
                  variant="contained"
                  size="large"
                  disabled={!rate || isSubmitting}
                  startIcon={isSubmitting ? <CircularProgress size={18} color="inherit" /> : <SendRoundedIcon />}
                  sx={(theme) => ({
                    minWidth: { sm: 190 },
                    whiteSpace: 'nowrap',
                    '&.Mui-disabled': {
                      color: theme.palette.text.disabled,
                      borderColor: theme.palette.divider,
                      backgroundColor: theme.palette.action.disabledBackground,
                      backgroundImage: 'none',
                      boxShadow: 'none',
                      opacity: 1,
                    },
                  })}>
                  {isSubmitting ? (
                    <FormattedMessage id="ticketSurvey.submitting" defaultMessage="Gönderiliyor…" />
                  ) : (
                    <FormattedMessage id="ticketSurvey.submit" defaultMessage="Değerlendirmeyi gönder" />
                  )}
                </Button>
              </Stack>
            </Stack>
          </Box>
        )}
      </>
    );
  };

  return <SurveyShell>{renderContent()}</SurveyShell>;
};

export default TicketSurvey;
